/**
 * Shared authentication utilities for cookie-based auth.
 *
 * The JWT access + refresh tokens live in httpOnly cookies set by the
 * backend; JS cannot read them. JS can only touch the CSRF cookie, which
 * acts as a same-origin "is a session in progress" signal.
 *
 * The legacy localStorage-backed `getAuthToken` / `setAuthToken` /
 * `getTokenPayload` helpers were removed on 2026-04-24 (audit finding
 * D-05). They kept the JWT JS-readable — exactly what an XSS exploit
 * needs to exfiltrate a session. If you're porting code that called
 * getTokenPayload(), switch to the /api/v1/auth/me endpoint instead.
 */

/**
 * Get CSRF token from cookie for X-CSRF-Token header use.
 *
 * Anchored to a cookie boundary on purpose: `portal_csrf_token` ends in
 * `csrf_token` and is set on the shared ".ddmarc.com" domain, so an unanchored
 * match hands back the portal's token and every state-changing request 403s
 * with "CSRF token mismatch".
 */
export function getCSRFToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match?.[1] ?? null;
}

/**
 * Clear auth by calling the backend logout endpoint, which clears all
 * httpOnly cookies. Also wipes the JS-readable CSRF cookie so
 * isAuthenticated() flips to false without waiting on the network round-trip.
 */
export function clearAuthToken(): void {
  // Expire the CSRF cookie immediately so isAuthenticated() returns false
  // on the next call; the backend will clear the httpOnly ones below.
  document.cookie = "csrf_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  fetch("/api/v1/auth/logout", {
    method: "POST",
    credentials: "include",
    headers: {
      "X-CSRF-Token": getCSRFToken() || "",
    },
  }).catch(() => {
    // Logout no longer requires auth server-side; network failures
    // shouldn't block the client redirect to login.
  });
}

/**
 * Check if a session is in progress. Presence of the CSRF cookie is our
 * proxy signal for "the httpOnly auth cookie is probably set" — the
 * backend sets them together and the CSRF cookie is the only one JS can see.
 */
export function isAuthenticated(): boolean {
  return !!getCSRFToken();
}

/**
 * Ensure a CSRF token cookie exists.
 *
 * A fresh tab loaded into the SPA may not have a csrf_token cookie yet — the
 * CSRF middleware only sets it on the response of some prior request, so a
 * client that immediately POSTs (e.g. to /auth/login) would be rejected
 * because it has nothing to echo in X-CSRF-Token. This helper fetches the
 * backend bootstrap endpoint once, after which getCSRFToken() returns a
 * usable value.
 *
 * Call this once on app mount, before any authenticated or state-changing
 * request. Safe to call repeatedly — the backend reuses the existing token
 * if the cookie is already present.
 */
export async function ensureCSRFToken(): Promise<string | null> {
  const existing = getCSRFToken();
  if (existing) return existing;

  try {
    const response = await fetch("/api/v1/auth/csrf-token", {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { csrf_token?: string };
    return data.csrf_token ?? getCSRFToken();
  } catch {
    return null;
  }
}

/** Verify authentication via API. Use for routes that need a fresh check. */
export async function verifyAuth(): Promise<boolean> {
  try {
    const response = await fetch("/api/v1/auth/me", {
      credentials: "include",
    });
    return response.ok;
  } catch {
    return false;
  }
}
